import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IconsModule, KENDO_ICONS } from '@progress/kendo-angular-icons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule, KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_LABELS, LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule, KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { Subscription } from 'rxjs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { KENDO_EDITOR } from '@progress/kendo-angular-editor';
import { GridModule } from '@progress/kendo-angular-grid';
import { PagerModule } from '@progress/kendo-angular-pager';
import { IconName, IconPrefix, IconProp } from '@fortawesome/fontawesome-svg-core';
import { KENDO_TOOLBAR } from '@progress/kendo-angular-toolbar';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { ConfirmModalComponent } from '../../common-components/confirm-modal/confirm-modal.component';
import { CasiKendoEditorComponent } from '../../common-components/casi-kendo-editor/casi-kendo-editor.component';
import { EditorImageUploadComponent } from '../../common-components/editor-image-upload/editor-image-upload.component';
import {
  CasiOwnerUser,
  CasiProjectLabel,
  CasiStoryEstimate, CasiStoryPriority, CasiStoryStatus,
  CasiStoryType, CasiUserStory, Project, User as UserModel
} from '../../models/models.lib';
import { LoggerService } from '../../shared/logger.service';
import { FormModes } from '../../common/constants.lib';
import { ProjectUserService } from '../../tracker/project-user.service';
import { StatusButtonComponent } from '../../common-components/status-button/status-button.component';

@Component({
  selector: 'casi-user-story-add',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    InputsModule,
    FormsModule,
    ReactiveFormsModule,
    IconsModule,
    ButtonsModule,
    InputsModule,
    LabelModule,
    DropDownsModule,
    AutoFocusDirective,
    FontAwesomeModule,
    GridModule,
    PagerModule,
    KENDO_EDITOR,
    KENDO_DROPDOWNS, KENDO_LABELS, KENDO_ICONS,
    KENDO_BUTTONS,
    KENDO_TOOLBAR,
    ConfirmModalComponent,
    CasiKendoEditorComponent,
    EditorImageUploadComponent,
    StatusButtonComponent
  ],
  templateUrl: './user-story-add.component.html',
  styleUrl: './user-story-add.component.scss'
})
export class UserStoryAddComponent implements OnInit, OnDestroy, AfterContentInit {

  @Input({ required: true }) projects: Project[] = [];
  @Input() showProjects: boolean = true;

  private _userStory: CasiUserStory | undefined;
  @Input({ required: true }) set userStory(val: CasiUserStory) {
    this._userStory = val;
    this.logger.debug(`input:userStory: `, this._userStory);
    this.form.patchValue(this._userStory);
    // this.formCode.patchValue(this._userStory);
    this.resetSelection();
    // this.selectedType = this._userStory.user_story_type_id!;
    // this.selectedPriority = this._userStory.user_story_priority_description!;
    // this.selectedRequester = this._userStory.requested_by_user_id!;
    // this.selectedEstimate = this._userStory.user_story_estimate_id;
    // this.selectedStatus = this._userStory.user_story_status_id!;
    // if (this.selectedStatus === 6) {
    //   this.removeValidationToOwners();
    // }
    // this.selectTypeIconClass = this.setStoryIcons();
    // if (this._userStory.project_id) {
    //   this.fetchProjectUsers(this._userStory.project_id);
    // }
  }
  get userStory() {
    return (this._userStory as CasiUserStory);
  }

  private _storyTypes: CasiStoryType[] = [];
  @Input({ required: true }) set storyTypes(val: CasiStoryType[]) {
    this._storyTypes = val;
    this.setStoryIcons();
  }
  get storyTypes() {
    return this._storyTypes;
  }
  @Input({ required: true }) storyStatuses: CasiStoryStatus[] = [];
  @Input({ required: true }) storyPriorities: CasiStoryPriority[] = [];
  @Input({ required: true }) storyEstimates: CasiStoryEstimate[] = [];
  @Input({ required: true }) users: UserModel[] = [];
  @Input() selectedUserStoryId: string | null = null;
  @Input({ required: true }) mode: FormModes = FormModes.NOTSET;

  private _projectLables: CasiProjectLabel[] = [];
  @Input({ required: true }) set projectLabels(val: CasiProjectLabel[]) {
    this.allProjectLabels = val;
    if (val) {
      this.setProjectLabels(val);
    } else {
      this.setProjectLabels([]);
    }
  }
  get projectLabels() {
    return this._projectLables;
  };

  @Output() submitForm: EventEmitter<FormGroup> = new EventEmitter();
  @Output() closeForm: EventEmitter<void> = new EventEmitter();

  @ViewChild("uploadDialog") public dialog: EditorImageUploadComponent = <EditorImageUploadComponent>{};

  //#region Drop down options

  public defaultDropdownItem: { text: string; id: number | undefined } = {
    text: "Select ...",
    id: undefined
  };

  public defaultStoryItem: { text: string; value: number | undefined } = {
    text: "Select ...",
    value: undefined
  };

  public defaultProjectItem: { project_name: string; id: number | undefined } = {
    project_name: "Select ...",
    id: undefined
  };

  public defaultStoryTypeItem: {
    text: string; id: number | null; user_story_type_description: string;
    iconClass: string | undefined; icon: string | undefined
  } = {
      text: "Select ...",
      id: null,
      user_story_type_description: '',
      iconClass: undefined,
      icon: undefined
    };

  public defaultRequesterItem: {
    text: string; id: number | null; fullName: string;
    initials: string;
  } = {
      text: "Select ...",
      id: null,
      fullName: '',
      initials: ''
    };

  //#endregion

  form: FormGroup;

  @ViewChild('pForm') pForm!: ElementRef;

  set userStoryProject(val: Project | undefined) {
    if (this._userStory) {
      this._userStory.project_id = val?.id;
      this._userStory.project_name = val?.project_name
      this.form.patchValue({ project_id: val?.id, project_name: val?.project_name });
    }
  }

  public selectedType: number | undefined; // = StoryTypes.Bug;
  public selectTypeIconClass: string | undefined;
  public selectedPriority: string | undefined; // = StoryPriorities.None;
  public selectedRequester: number | undefined;
  public selectedStatus: number | undefined;
  public selectedStoryStatus: CasiStoryStatus | undefined;
  public selectedEstimate: number | undefined;
  public modUsers: CasiOwnerUser[] = [];

  public iconList: IconProp | undefined;
  public expandedState = false;

  public readonlyDescription: boolean = true;
  public readonlyCode: boolean = true;


  private allProjectLabels: CasiProjectLabel[] = [];
  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    public faIcons: FaIconLibrary,
    private fb: FormBuilder,
    private projectUserService: ProjectUserService
  ) {
    faIcons.addIcons(...all);
    this.form = this.fb.group(
      {
        id: [null, [Validators.required]],
        project_id: [null, [Validators.required]],
        project_name: [null, []],
        title: [null, [Validators.required]],
        description: [null, [Validators.required]],
        user_story_status_id: [null, [Validators.required]],
        user_story_status_description: [null, []],
        user_story_type_id: [null, [Validators.required]],
        user_story_priority_id: [null, [Validators.required]],
        user_story_estimate_id: [null, [Validators.required]],
        requested_by_user: [null, []],
        requested_by_user_id: [null, []],
        owned_by_users_json: [null, []],
        owned_by_users: [null, []],
        owned_by_user_ids: [null, [Validators.required]],
        project_label_id: [null, []],
        accepted_at: [null, []],
        deadline: [null, []],
        updated_by_user_id: [],
        updated_by_user: [],
        itemMode: [null, []]
      }
    );
  }

  //#region NG Event Handlers

  ngOnInit(): void {
    // this.logger.debug('ngOnInit...');
  }

  ngOnDestroy(): void {
    // this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  ngAfterContentInit(): void {
    // this.logger.debug('ngAfterContentInit...');
  }

  //#endregion

  //#region Form Properties

  get f() {
    return this.form.controls;
  }
  get titleIsDirty() {
    return this.form.dirty;
  }
  get id() {
    return this.form.get('id');
  }
  get idValue() {
    return this.form.get('id')?.value;
  }
  get project_id() {
    return this.form.get('project_id');
  }
  get project_name() {
    return this.form.get('project_name');
  }
  get title() {
    return this.form.get('title');
  }
  get description() {
    return this.form.get('description');
  }
  get description_value() {
    return this.form.get('description')?.value;
  }
  get user_story_status_id() {
    return this.form.get('user_story_status_id');
  }
  get user_story_status_id_value() {
    return this.form.get('user_story_status_id')?.value;
  }
  get user_story_type_id() {
    return this.form.get('user_story_type_id');
  }
  get user_story_type_id_value() {
    return this.form.get('user_story_type_id')?.value;
  }
  get user_story_priority_id() {
    return this.form.get('user_story_priority_id');
  }
  get user_story_priority_id_value() {
    return this.form.get('user_story_priority_id')?.value;
  }
  get user_story_estimate_id() {
    return this.form.get('user_story_estimate_id');
  }
  get user_story_estimate_id_value() {
    return this.form.get('user_story_estimate_id')?.value;
  }
  get requested_by_user() {
    return this.form.get('requested_by_user');
  }
  get requested_by_user_value() {
    return this.form.get('requested_by_user')?.value;
  }
  get requested_by_user_id() {
    return this.form.get('requested_by_user_id');
  }
  get requested_by_user_id_value() {
    return this.form.get('requested_by_user_id')?.value;
  }
  get owned_by_user_ids() {
    return this.form.get('owned_by_user_ids');
  }

  //#endregion

  //#region Public functions

  public onStatusButtonChange(evnt: any) {
    this.logger.debug(`onStatusButtonChange:evnt: `, evnt);
    this.updateSelectionStatus(evnt, true);
    this.form.markAsDirty();
  }

  onDescriptionChange(evnt: any): void {
    // this.logger.debug(`onDescriptionChange: evnt: `, evnt);
    this.userStory.description = evnt;
    this.form.patchValue({ description: evnt });
  }

  onSubmit(): void {
    this.logger.debug(`onSubmit...`, this.form.value, this.mode);
    this.submitForm.emit(this.form);
  }

  closeWindow(): void {
    this.logger.debug(`closeWindow...`);
    this.closeForm.emit();
  }

  public projectValueChange(value: any): void {
    this.logger.debug(`projectValueChange...`, value);
    this.setCurrentProject(value);
    // this.form.patchValue({ project_name: value.project_name });
  }

  public openImageUpload() {
    setTimeout(() => this.dialog.open(), 200);
  }

  public onImageLoaded(args: any): void {
    this.logger.debug('onImageLoaded...', args);
    const { data } = args;
  }

  public selectionChange(evnt: any) {
    // this.logger.debug(`selectionChange: `, evnt);
  }

  public selectionStatusChange(evnt: any) {
    this.logger.debug(`selectionStatusChange: `, evnt);
    if (evnt.user_story_status_description.toLowerCase() === 'unscheduled' || evnt.user_story_status_description.toLowerCase() === 'icebox') {
      this.removeValidationToOwners();
    } else {
      this.addValidationToOwners();
    }
  }

  public selectionPriorityChange(evnt: any) {
    // this.logger.debug(`selectionPriorityChange: `, evnt);
  }

  public selectionEstimateChange(evnt: any) {
    // this.logger.debug(`selectionEstimateChange: `, evnt);
  }

  public selectionRequesterChange(evnt: any) {
    // this.logger.debug(`selectionRequesterChange: `, evnt);
  }

  //#endregion

  //#region Private functions

  private addValidationToOwners() {
    this.owned_by_user_ids?.setValidators([Validators.required]);
    this.owned_by_user_ids?.updateValueAndValidity();
  }

  private removeValidationToOwners() {
    this.owned_by_user_ids?.setValidators(null);
    this.owned_by_user_ids?.updateValueAndValidity();
  }

  private resetSelection(): void {
    this.selectedType = this._userStory!.user_story_type_id!;
    this.selectedPriority = this._userStory!.user_story_priority_description!;
    this.selectedRequester = this._userStory!.requested_by_user_id!;
    // this.selectedProjectId = this._userStory!.project_id;
    // this.selectedOwners = this._userStory!.owned_by_users_json!;
    this.selectedEstimate = this._userStory!.user_story_estimate_id;
    this.selectedStatus = this._userStory!.user_story_status_id!;
    if (this.selectedStatus === 6) {
      this.removeValidationToOwners();
    }
    this.setSelectedStoryStatus(this.selectedStatus);
    this.selectTypeIconClass = this.setStoryIcons();
    this.fetchProjectUsers(this._userStory?.project_id);
  }

  private setStoryIcons(): any {
    // this.logger.debug(`setStoryIcons...storyTypes:`, this.storyTypes);
    // this.logger.debug(`setStoryIcons...selectedType`, this.selectedType);
    if (this.storyTypes) {
      let found = this.storyTypes.find(st => st.id === this.selectedType);
      if (found) {
        this.iconList = [('fad' as IconPrefix), (found.icon as IconName)];
        setTimeout(() => {
          this.selectTypeIconClass = found.iconClass;
        });
      }
    }
  }

  private setProjectLabels(vals: CasiProjectLabel[]) {
    this.logger.debug(`setProjectLabels:val: `, vals);
    if (this.userStory && this.userStory.project_id) {
      this._projectLables = vals.filter((p) => p.project_id === this.userStory?.project_id);
    } else {
      this._projectLables = vals;
    }
    // this.logger.debug(`setProjectLables: `, this.projectLabels);
  }

  private setCurrentProject(project?: Project) {
    this.logger.debug(`setCurrentProject: `, project);
    if (project && project.id) {
      this.userStoryProject = project;
      this.fetchProjectUsers(project.id);
    } else {
      this.userStoryProject = undefined;
    }
    this.setProjectLabels(this.allProjectLabels);
  }

  private fetchProjectUsers(projectId: number | undefined): void {
    if (projectId)
      this.projectUserService.getByProjectId(projectId)
        .then(resp => {
          this.modUsers = resp.map((r: any) => CasiOwnerUser.createFromProjectUser(r));
        })
        .catch(err => {
          this.logger.debug(`fetchProjectUsers: `, err.message);
        })
  }

  private updateSelectionStatus(evnt: any, doId: boolean = false) {
    const { id, nextUserStoryId, user_story_status_description, userStoryAction } = evnt;
    if (doId) {
      this.form.patchValue({
        user_story_status_id: id ? id : nextUserStoryId
      });
    }
    this.form.patchValue({
      user_story_status_description: user_story_status_description ? user_story_status_description : userStoryAction
    });
    this.selectedStatus = id ? id : nextUserStoryId;
    this.setSelectedStoryStatus(this.selectedStatus!);
    // this.saveUserStoryChanges();
    // this.doNotify();
    if ((user_story_status_description ? user_story_status_description : userStoryAction).toLowerCase() === 'unscheduled') {
      this.removeValidationToOwners();
    } else {
      this.addValidationToOwners();
    }
  }

  private findStoryStatus(id: number) {
    return this.storyStatuses.find(ss => ss.id === id);
  }

  private setSelectedStoryStatus(id: number) {
    this.selectedStoryStatus = this.findStoryStatus(id); // this.storyStatuses.find(ss => ss.id === id);
    this.logger.debug(`setSelectedStoryStatus: `, this.selectedStoryStatus);
  }

  //#endregion

}

